import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { User, AuthResponse } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/v1/auth';

  readonly currentUser = signal<User | null>(null);
  readonly isLoggedIn  = computed(() => this.currentUser() !== null);

  register(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/register`, { email, password })
      .pipe(tap(r => this.handleResponse(r)));
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/login`, { email, password })
      .pipe(tap(r => this.handleResponse(r)));
  }

  verify(email: string, code: string, purpose: 'login' | 'register'): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/verify`, { email, code, purpose })
      .pipe(tap(r => this.handleResponse(r)));
  }

  resendCode(email: string, purpose: 'login' | 'register'): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.base}/resend-code`, { email, purpose });
  }

  logout(): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/logout`, {})
      .pipe(tap(() => this.currentUser.set(null)));
  }

  checkStatus(): Observable<AuthResponse> {
    return this.http.get<AuthResponse>(`${this.base}/status`).pipe(
      tap(r => this.currentUser.set(r.loggedIn && r.user ? r.user : null)),
    );
  }

  handleUnauthorized(): void {
    this.currentUser.set(null);
  }

  private handleResponse(r: AuthResponse): void {
    if (r.success && r.user) this.currentUser.set(r.user);
  }
}
